import { useCallback, useEffect, useMemo, useRef } from 'react'

import { EMPTY_LIST, EMPTY_STRING } from '../../../../../../constants/common'
import {
  isEventKeyEnter,
  isEventKeySpaceBar,
  preventDefault,
} from '../../../../../../utils/events'
import { getEmailRecipientsCorrectValue } from '../../../../Email.utils'
import {
  getRecipientItemId,
  isValidEmail,
  splitValidAndInvalidEmails,
} from '../../../EmailRecipientsField.utils'
import {
  getUpdatedRecipientsValue,
  isEmailInputComplete,
} from '../../EmailRecipientsSelect.utils'

import {
  deselectRecipient,
  getInputValueWithPastedText,
  isSelectedRecipient,
  selectRecipient,
} from './useEmailRecipientsSelect.utils'

export function useEmailRecipientsSelect({
  clearSearchInSelect,
  closeSelect,
  inputElRef,
  selectStatesRef,
  value,
  onChange,
  onExpandSelectedRecipientEmails,
  onValidate,
}) {
  const skipCloseRef = useRef(false)

  const hasInvalidItem = useMemo(
    () => !!value.find(i => !i.value || !isValidEmail(i.email)),
    [value]
  )

  const updateValue = useCallback(
    nextItems => {
      onChange(getEmailRecipientsCorrectValue(nextItems || EMPTY_LIST))
    },
    [onChange]
  )

  const updateInputValue = useCallback(
    (inputValue = EMPTY_STRING, { close = false }) => {
      const input = inputValue.trim()

      if (!input) {
        if (close) {
          closeSelect()
        }

        return
      }

      const { items: nextItems } = getUpdatedRecipientsValue(input, value)

      updateValue(nextItems)

      if (nextItems.length > value.length) {
        if (selectStatesRef.current?.isOpen) {
          skipCloseRef.current = true
        }

        clearSearchInSelect()
      }

      onValidate?.(input)

      if (close) {
        skipCloseRef.current = true
        closeSelect()
      }
    },
    [onValidate, updateValue, value]
  )

  useEffect(() => {
    if (
      selectStatesRef.current?.searchValue &&
      isEmailInputComplete(selectStatesRef.current?.searchValue)
    ) {
      skipCloseRef.current = true
      updateInputValue(selectStatesRef.current?.searchValue, { close: false })
    }
    // selectStatesRef.current.searchValue is updated by InputSelect inside a
    // useEffect that depends on the source state, so parent re-renders and
    // this hook re-evaluates the deps array — comparing the latest read
    // against the previous one. Lifting it out of the ref would require a
    // refactor of the Select prop contract; tracked separately.
    // eslint-disable-next-line react-hooks/refs
  }, [selectStatesRef.current?.searchValue])

  const handleChangeSearch = useCallback(
    nextSearch => {
      // an emptied input must not keep stale validation notifications around
      if (!nextSearch) {
        onValidate?.(EMPTY_STRING)
      }
    },
    [onValidate]
  )

  const handleKeyDown = useCallback(
    e => {
      if (isEventKeyEnter(e) || isEventKeySpaceBar(e)) {
        const val = e.target.value
        const shouldCreateTag =
          !!val &&
          (splitValidAndInvalidEmails(val).valid.length > 0 ||
            isEmailInputComplete(val))

        // space stays typeable unless it completes an email into a tag
        if (isEventKeyEnter(e) || shouldCreateTag) {
          preventDefault(e)

          if (shouldCreateTag) {
            updateInputValue(val, { close: false })
          }
        }
      }
    },
    [updateInputValue]
  )

  function handlePaste(e) {
    const input = getInputValueWithPastedText(e)

    if (splitValidAndInvalidEmails(input).valid.length > 0) {
      preventDefault(e)
      updateInputValue(input, { close: false })
    }
  }

  function handleBlur(e) {
    updateInputValue(e.target.value, { close: false })
  }

  const handleSelectItem = useCallback(
    selected => {
      if (!selected) {
        return
      }

      skipCloseRef.current = true

      const selectedKey = getRecipientItemId(selected)
      const nextItems = isSelectedRecipient(value, selectedKey)
        ? deselectRecipient(value, selectedKey)
        : selectRecipient(value, selected, onExpandSelectedRecipientEmails)

      updateValue(nextItems)

      clearSearchInSelect()

      inputElRef.current?.focus?.()
    },
    [onExpandSelectedRecipientEmails, updateValue, value]
  )

  const handleDeleteOption = useCallback(
    option => {
      const optionKey = getRecipientItemId(option)
      const nextItems = deselectRecipient(value, optionKey)

      updateValue(nextItems)
    },
    [updateValue, value]
  )

  const handleClearAll = useCallback(() => {
    updateValue(EMPTY_LIST)

    if (selectStatesRef.current?.isOpen) {
      skipCloseRef.current = true
    }

    clearSearchInSelect()
  }, [updateValue])

  const handleCloseDropdown = useCallback(() => {
    if (skipCloseRef.current) {
      skipCloseRef.current = false

      return
    }

    const currentSearch = selectStatesRef.current?.searchValue

    if (!(currentSearch || EMPTY_STRING).trim()) {
      return
    }

    updateInputValue(currentSearch.trim(), { close: false })
  }, [hasInvalidItem, onValidate, updateValue])

  return {
    handleBlur,
    handleChangeSearch,
    handleClearAll,
    handleCloseDropdown,
    handleDeleteOption,
    handleKeyDown,
    handlePaste,
    handleSelectItem,
    updateValue,
  }
}
